import React, { useState } from 'react';
import { MessageSquare, Send, Clock } from 'lucide-react';
import { submitToHubSpot } from '../utils/hubspot';
import { logger } from '../utils/logging';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  hoaName: string;
  message: string;
}

interface ContactCTAProps {
  onSuccess?: () => void;
  onError?: () => void;
}

export function ContactCTA({ onSuccess, onError }: ContactCTAProps) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    hoaName: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      logger.info('Submitting contact form', { 
        hoaName: formData.hoaName,
        email: formData.email 
      });

      await submitToHubSpot(formData);
      onSuccess?.();
    } catch (error) {
      logger.error('Contact form submission failed', error instanceof Error ? error : new Error('Unknown error'));
      onError?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "block w-full px-4 py-3 rounded-lg border-2 border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 ease-in-out";
  const labelClasses = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="bg-white">
      <div className="p-6">
        <div className="flex flex-col items-center text-center mb-8 pb-8 border-b border-gray-200">
          <img
            src="https://media.licdn.com/dms/image/v2/D4E03AQG2-WhuK-6v3A/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1675363670379?e=1744243200&v=beta&t=N1iwysrKhFVU1oVpthrxEkwC86TNDo89KJWLPtXqsbw"
            alt="Aaron Sheklin"
            className="w-24 h-24 rounded-full border-4 border-white shadow-lg mb-4"
          />
          <h3 className="text-xl font-semibold text-gray-900">Aaron Sheklin</h3>
          <p className="text-gray-600">Senior Investment Advisor</p>
          <div className="flex items-center gap-2 mt-2 text-gray-500">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Usually responds within 24 hours</span>
          </div>
          <p className="mt-4 text-gray-600 max-w-lg">
            With over 15 years of experience in HOA investment management, I'm here to help you optimize your reserve fund strategy and maximize returns while maintaining appropriate risk levels.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Schedule a Consultation</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className={labelClasses}>
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                id="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
            
            <div>
              <label htmlFor="lastName" className={labelClasses}>
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                id="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className={labelClasses}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
            
            <div>
              <label htmlFor="phone" className={labelClasses}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
          </div>

          <div>
            <label htmlFor="hoaName" className={labelClasses}>
              HOA Name *
            </label>
            <input
              type="text"
              name="hoaName"
              id="hoaName"
              required
              value={formData.hoaName}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>

          <div>
            <label htmlFor="message" className={labelClasses}>
              Message *
            </label>
            <textarea
              name="message"
              id="message"
              rows={4}
              required
              value={formData.message}
              onChange={handleChange}
              className={inputClasses}
              placeholder="Tell us about your HOA's investment needs..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}